import { useState } from "react";
import { useInstallPrompt, isIOS } from "@/hooks/useInstallPrompt";

export default function StudioDownloadPanel() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const { available, promptInstall, isStandalone } = useInstallPrompt();
  const ios = isIOS();

  async function downloadStandalone() {
    setBusy(true); setMsg(null);
    try {
      const resp = await fetch("/creator-record.html", { cache: "no-store" });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const html = await resp.text();
      if (html.length < 20000) throw new Error("file is corrupted · " + html.length + " bytes");
      const blob = new Blob([html], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "YUO_Studio_Offline.html";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      setMsg({ kind: "ok", text: `File saved (${(blob.size / 1024).toFixed(0)} KB). Double-click to open it as a local app.` });
    } catch (e) {
      setMsg({ kind: "err", text: "Download failed: " + e.message + ". Use the direct link below." });
    } finally { setBusy(false); }
  }

  async function onInstall() {
    const r = await promptInstall();
    if (r?.outcome === "accepted") setMsg({ kind: "ok", text: "YUO installed — the icon will appear among your apps" });
    else if (r?.outcome === "unavailable") setMsg({ kind: "err", text: "In this browser use the “Install YUO Studio” menu item or the icon in the address bar" });
  }

  const capsule = {
    display: "inline-flex", alignItems: "center", gap: 10,
    padding: "12px 20px", borderRadius: 999, cursor: "pointer",
    fontFamily: '"JetBrains Mono", ui-monospace, monospace',
    fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase",
    color: "rgba(var(--role-green-rgb),.98)",
    background: "radial-gradient(120% 160% at 30% 0%, rgba(var(--role-green-rgb),.28), rgba(var(--role-green-rgb),.06))",
    boxShadow: "0 0 28px rgba(var(--role-green-rgb),.28)", border: "none",
    whiteSpace: "nowrap",
  };
  const sect = {
    padding: "20px 22px", borderRadius: 24,
    background: "radial-gradient(120% 100% at 26% 0%, var(--fg-05), var(--fg-01) 60%, transparent 100%)",
    backdropFilter: "blur(14px)",
  };
  const h = { fontFamily: '"JetBrains Mono", ui-monospace, monospace', fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase", color: "var(--fg-42)", marginBottom: 12 };
  const p = { fontSize: 13, lineHeight: 1.7, color: "var(--fg-7)" };
  const bullet = { ...p, paddingLeft: 18, position: "relative" };

  return (
    <div data-testid="studio-download-block" style={{ marginBottom: 20 }}>
      <button
        data-testid="open-studio-download-btn"
        onClick={() => setOpen(v => !v)}
        style={capsule}
      >
        <span style={{
          width: 16, height: 16, borderRadius: "50%",
          background: "radial-gradient(34% 30% at 32% 26%, #fff, #4DFFA0 22%, #0D3A26 62%, #01040a 100%)",
          boxShadow: "0 0 12px rgba(var(--role-green-rgb),.75)",
        }} />
        ⤓ Download YUO to your computer · terms and installation
      </button>

      {open && (
        <div data-testid="studio-download-panel" style={{
          marginTop: 16, display: "flex", flexDirection: "column", gap: 14,
          animation: "yuoReveal .5s both",
        }}>
          {/* A — INSTALL AS APP */}
          <div style={sect}>
            <div style={h}>A · install YUO as an app</div>
            <div style={{ ...p, marginBottom: 12 }}>
              The YUO icon will appear in your apps list (Mac / Windows) or on the Home screen (iPad). The app opens in a clean window without the browser bar.
            </div>
            {isStandalone ? (
              <div style={{ ...p, color: "rgba(198,255,224,.8)" }}>✓ YUO is already installed as an app.</div>
            ) : ios ? (
              <div>
                <div style={bullet}>1 · Tap <b style={{ color: "rgba(var(--role-green-rgb),.9)" }}>“Share”</b> ⤴︎ in the Safari bar</div>
                <div style={bullet}>2 · Choose <b style={{ color: "rgba(var(--role-green-rgb),.9)" }}>“Add to Home Screen”</b></div>
                <div style={bullet}>3 · Tap <b>“Add”</b> — YUO appears with its own icon</div>
              </div>
            ) : (
              <>
                <button
                  data-testid="studio-install-btn"
                  onClick={onInstall}
                  disabled={!available}
                  style={{
                    ...capsule, opacity: available ? 1 : 0.45,
                    background: "radial-gradient(120% 160% at 30% 0%, rgba(var(--role-green-rgb),.34), rgba(var(--role-green-rgb),.08))",
                    boxShadow: "0 0 34px rgba(var(--role-green-rgb),.32)",
                  }}
                >
                  {available ? "⤓ Install YUO as an app" : "use “Install YUO Studio” in the browser menu"}
                </button>
                <div style={{ ...p, marginTop: 10, fontSize: 11, color: "var(--fg-4)" }}>
                  If the button is inactive — open the Chrome / Arc / Edge menu → <b>“Install YUO Studio”</b> (or the ⊕ icon on the right of the address bar).
                </div>
              </>
            )}
          </div>

          {/* B — DOWNLOAD OFFLINE HTML */}
          <div style={sect}>
            <div style={h}>B · download the standalone HTML (offline)</div>
            <div style={{ ...p, marginBottom: 12 }}>
              A complete standalone file with the whole studio (recording, markup, palette, IndexedDB). Opens locally without internet in Chrome / Safari / Arc / Edge.
            </div>
            <button
              data-testid="studio-download-html-btn"
              onClick={downloadStandalone}
              disabled={busy}
              style={{
                ...capsule, opacity: busy ? 0.5 : 1,
                background: "radial-gradient(120% 160% at 30% 0%, rgba(var(--role-green-rgb),.22), rgba(var(--role-green-rgb),.05))",
                boxShadow: "0 0 22px rgba(var(--role-green-rgb),.22)",
              }}
            >
              {busy ? "…" : "⤓ download the .html file"}
            </button>
            <a
              data-testid="studio-direct-link"
              href="/creator-record.html"
              download="YUO_Studio_Offline.html"
              style={{
                display: "inline-block", marginLeft: 10,
                padding: "10px 16px", borderRadius: 999,
                fontSize: 10.5, letterSpacing: "0.18em", textTransform: "uppercase",
                color: "var(--fg-6)", textDecoration: "none",
                background: "var(--fg-05)",
              }}
            >
              direct link ↗
            </a>
            {msg && (
              <div style={{
                marginTop: 12, padding: "10px 14px", borderRadius: 16,
                fontSize: 11.5, lineHeight: 1.6,
                color: msg.kind === "ok" ? "rgba(198,255,224,.85)" : "rgba(255,180,170,.9)",
                background: msg.kind === "ok" ? "rgba(var(--role-green-rgb),.08)" : "rgba(255,80,60,.1)",
              }}>{msg.text}</div>
            )}
          </div>

          {/* C — RIGHTS & PRIVACY */}
          <div style={sect}>
            <div style={h}>C · rights and privacy</div>
            <div style={{ ...bullet, marginBottom: 8 }}>
              <b style={{ color: "rgba(var(--role-green-rgb),.9)" }}>100% local privacy.</b> All video streams, screen recording, audio and drafts are processed only inside your browser (Canvas API + IndexedDB).
            </div>
            <div style={{ ...bullet, marginBottom: 8 }}>
              <b style={{ color: "rgba(var(--role-green-rgb),.9)" }}>Zero server upload.</b> Not a single frame, sound, slide or note leaves for third parties or remote servers.
            </div>
            <div style={bullet}>
              <b style={{ color: "rgba(var(--role-green-rgb),.9)" }}>Copyright.</b> All created videos, takes and files are the 100% exclusive property of the Creator.
            </div>
            <div style={{ ...p, marginTop: 14, fontSize: 11, color: "var(--fg-4)" }}>
              If something fails during recording, the draft is saved automatically every 5 seconds.
            </div>
          </div>
        </div>
      )}
      <style>{`@keyframes yuoReveal{from{opacity:0;transform:translateY(-6px);filter:blur(8px)}to{opacity:1;transform:none;filter:blur(0)}}`}</style>
    </div>
  );
}
